import type { LoginInfo } from "@/types/LoginInfo";
import type { RouteCondition } from "@/types/RouteConfig";
import { FeatureKey } from "@/types/generated/FeatureKey";
import { hasFeature } from "@hooks/useFeatures";
import { isSystemUser } from "@/utils/orgTypeUtils";
import { loginHasDirectBusiness, loginIsCommercialChannelPartner } from "@/utils/directBusiness";

/**
 * STIC-16004: route-level guards for the pages the sidebar hides by partner
 * variant, so a deep link cannot bypass the menu.
 *
 * They answer "may this login use these pages at all?", not "is this page
 * useful for the organisation currently browsed" - that second question is the
 * page's own, and the pages already answer it by rendering
 * `NotAvailableAtThisLevel` (see Invoicing.tsx). Keeping the two apart is what
 * lets a cross-level entry be greyed rather than bounced to /no_access.
 *
 * Deliberately synchronous, and deliberately blind to the browsed `?org=`.
 * Judging that organisation here would mean fetching it, and its answer belongs
 * to the page anyway: a partner browsing one of its customers gets the greyed
 * menu entry and the page's "not available" message, not a bounce to
 * /no_access.
 */

/**
 * Purchase orders, Support tickets, Billing and subscriptions, invoice and
 * quotation details: the organisation's own business with Sensolus. Sensolus
 * reads them for any organisation; everyone else only when its own organisation
 * deals with Sensolus directly (see directBusiness.ts).
 */
export const DIRECT_BUSINESS_ACCESS: RouteCondition = ({ loginInfo }) =>
  isSystemUser(loginInfo) || loginHasDirectBusiness(loginInfo);

/**
 * Demos: a presales tool for Sensolus and for commercial channel partners, at
 * their own level. The partner Quotes add the EU price list on top and keep
 * their own condition in quoteAccess.ts; the Battery calculator adds a feature
 * key and keeps its own condition below.
 */
export const PRESALES_ACCESS: RouteCondition = ({ loginInfo }) =>
  isSystemUser(loginInfo) || loginIsCommercialChannelPartner(loginInfo);

/**
 * The Battery calculator (STIC-16282). A presales tool like the Demos, with one
 * way in on top: the `battery_calculator` feature key, granted by hand to a
 * partner that is not a commercial channel partner (a multi-org such as a large
 * customer, or a non-commercial channel partner). Read against the login's own
 * organisation - the tool has no browsed
 * organisation, it sizes a product.
 *
 * Shared by the sidebar entry and the route guard so the two cannot drift: a
 * deep link has to answer the same question the menu does.
 */
export const canUseBatteryCalculator = (loginInfo: LoginInfo | null | undefined): boolean =>
  isSystemUser(loginInfo) ||
  loginIsCommercialChannelPartner(loginInfo) ||
  hasFeature(FeatureKey.battery_calculator, loginInfo ?? null);

export const BATTERY_CALCULATOR_ACCESS: RouteCondition = ({ loginInfo }) =>
  canUseBatteryCalculator(loginInfo);
